import { useState, useEffect, useCallback, useMemo } from "react";
import SortableTableHead, { SortConfig, nextSort, sortData } from "./SortableTableHead";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DollarSign,
  Users,
  TrendingUp,
  RefreshCw,
  Eye,
  UserCheck,
  Download,
  CalendarIcon,
} from "lucide-react";
import { format, subDays, startOfDay, endOfDay, isWithinInterval } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface Order {
  id: string;
  patient_id: string | null;
  patient_email: string | null;
  patient_name: string | null;
  order_type: string;
  status: string;
  amount_cents: number;
  commission_rate: number;
  salesperson_id: string | null;
  created_at: string;
}

interface Profile {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
}

interface SalespersonSummary {
  id: string;
  name: string;
  totalRevenue: number;
  totalCommission: number;
  uniqueCustomers: number;
  orderCount: number;
  activeSubscriptions: number;
  orders: Order[];
}

const SalesAttribution = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSp, setSelectedSp] = useState<SalespersonSummary | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(subDays(new Date(), 30));
  const [dateTo, setDateTo] = useState<Date | undefined>(new Date());
  const [salesSort, setSalesSort] = useState<SortConfig>({ key: "totalRevenue", direction: "desc" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [ordersRes, profilesRes] = await Promise.all([
      supabase
        .from("orders")
        .select("id, patient_id, patient_email, patient_name, order_type, status, amount_cents, commission_rate, salesperson_id, created_at")
        .not("salesperson_id", "is", null)
        .order("created_at", { ascending: false })
        .limit(1000),
      supabase.from("profiles").select("user_id, first_name, last_name"),
    ]);
    setOrders((ordersRes.data as Order[]) || []);
    setProfiles((profilesRes.data as Profile[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter orders by date range (must be before early return)
  const filteredOrders = useMemo(() => {
    if (!dateFrom && !dateTo) return orders;
    return orders.filter((o) => {
      const d = new Date(o.created_at);
      if (dateFrom && d < startOfDay(dateFrom)) return false;
      if (dateTo && d > endOfDay(dateTo)) return false;
      return true;
    });
  }, [orders, dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const profileMap = new Map(profiles.map((p) => [p.user_id, p]));

  const getName = (userId: string) => {
    const p = profileMap.get(userId);
    if (p?.first_name || p?.last_name) return `${p.first_name || ""} ${p.last_name || ""}`.trim();
    return userId.slice(0, 8) + "…";
  };

  // Group orders by salesperson
  const spMap = new Map<string, Order[]>();
  filteredOrders.forEach((o) => {
    if (!o.salesperson_id) return;
    const list = spMap.get(o.salesperson_id) || [];
    list.push(o);
    spMap.set(o.salesperson_id, list);
  });

  const summaries: SalespersonSummary[] = sortData(Array.from(spMap.entries())
    .map(([spId, spOrders]) => {
      const active = spOrders.filter((o) => !["cancelled", "refunded"].includes(o.status));
      const customers = new Set(spOrders.map((o) => o.patient_email || o.patient_id).filter(Boolean));
      return {
        id: spId,
        name: getName(spId),
        totalRevenue: active.reduce((s, o) => s + o.amount_cents, 0),
        totalCommission: active.reduce((s, o) => s + (o.amount_cents * o.commission_rate) / 100, 0),
        uniqueCustomers: customers.size,
        orderCount: spOrders.length,
        activeSubscriptions: spOrders.filter((o) => o.order_type === "subscription" && o.status === "active").length,
        orders: spOrders,
      };
    }), salesSort.key ? salesSort : { key: "totalRevenue", direction: "desc" as const });

  const totalAttrRevenue = summaries.reduce((s, sp) => s + sp.totalRevenue, 0);
  const totalCommissions = summaries.reduce((s, sp) => s + sp.totalCommission, 0);
  const totalCustomers = new Set(filteredOrders.map((o) => o.patient_email || o.patient_id).filter(Boolean)).size;

  const fmt = (cents: number) => `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const exportCSV = () => {
    const headers = ["Salesperson", "Salesperson ID", "Orders", "Customers", "Active Subscriptions", "Revenue ($)", "Commission ($)", "Commission Rate (avg %)"];
    const rows = summaries.map((sp) => {
      const avgRate = sp.orders.length > 0
        ? (sp.orders.reduce((s, o) => s + o.commission_rate, 0) / sp.orders.length).toFixed(1)
        : "0";
      return [
        sp.name,
        sp.id,
        sp.orderCount,
        sp.uniqueCustomers,
        sp.activeSubscriptions,
        (sp.totalRevenue / 100).toFixed(2),
        (sp.totalCommission / 100).toFixed(2),
        avgRate,
      ].join(",");
    });
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `commission-payout-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Chart data - top salespeople
  const chartData = summaries.slice(0, 8).map((sp) => ({
    name: sp.name.length > 12 ? sp.name.slice(0, 12) + "…" : sp.name,
    revenue: sp.totalRevenue / 100,
    commission: sp.totalCommission / 100,
  }));

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <Card className="border-border">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <CalendarIcon size={16} className="text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Date Range:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("w-[150px] justify-start text-left font-normal", !dateFrom && "text-muted-foreground")}>
                {dateFrom ? format(dateFrom, "MMM d, yyyy") : "Start date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus className="p-3 pointer-events-auto" />
            </PopoverContent>
          </Popover>
          <span className="text-muted-foreground text-sm">to</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("w-[150px] justify-start text-left font-normal", !dateTo && "text-muted-foreground")}>
                {dateTo ? format(dateTo, "MMM d, yyyy") : "End date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateTo} onSelect={setDateTo} initialFocus className="p-3 pointer-events-auto" />
            </PopoverContent>
          </Popover>
          <div className="flex items-center gap-1.5 ml-auto">
            {[
              { label: "7d", days: 7 },
              { label: "30d", days: 30 },
              { label: "90d", days: 90 },
              { label: "All", days: 0 },
            ].map((preset) => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                className="text-xs h-7 px-2"
                onClick={() => {
                  if (preset.days === 0) { setDateFrom(undefined); setDateTo(undefined); }
                  else { setDateFrom(subDays(new Date(), preset.days)); setDateTo(new Date()); }
                }}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Attributed Revenue", value: fmt(totalAttrRevenue), icon: DollarSign, color: "text-primary" },
          { label: "Total Commissions", value: fmt(totalCommissions), icon: TrendingUp, color: "text-champagne" },
          { label: "Unique Customers", value: totalCustomers, icon: Users, color: "text-foreground" },
          { label: "Active Salespeople", value: summaries.length, icon: UserCheck, color: "text-primary" },
        ].map((k) => (
          <Card key={k.label} className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{k.label}</span>
                <k.icon size={16} className={k.color} />
              </div>
              <p className="font-display text-2xl text-foreground">{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Revenue by Salesperson Chart */}
      {chartData.length > 0 && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <TrendingUp size={16} /> Revenue by Salesperson
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}
                  formatter={(value: number, name: string) => [`$${value.toFixed(2)}`, name === "revenue" ? "Revenue" : "Commission"]}
                />
                <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="revenue" />
                <Bar dataKey="commission" fill="hsl(var(--champagne))" radius={[4, 4, 0, 0]} name="commission" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Salesperson Leaderboard */}
      <Card className="border-border">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="font-display text-lg">Salesperson Leaderboard</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1" disabled={summaries.length === 0}>
              <Download size={14} /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData} className="gap-1">
              <RefreshCw size={14} /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {summaries.length === 0 ? (
            <p className="text-center text-muted-foreground py-12 text-sm">No attributed sales yet</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                     <TableHead>#</TableHead>
                    <SortableTableHead label="Salesperson" sortKey="name" currentSort={salesSort} onSort={(k) => setSalesSort(nextSort(salesSort, k))} />
                    <SortableTableHead label="Orders" sortKey="orderCount" currentSort={salesSort} onSort={(k) => setSalesSort(nextSort(salesSort, k))} />
                    <SortableTableHead label="Customers" sortKey="uniqueCustomers" currentSort={salesSort} onSort={(k) => setSalesSort(nextSort(salesSort, k))} />
                    <SortableTableHead label="Active Subs" sortKey="activeSubscriptions" currentSort={salesSort} onSort={(k) => setSalesSort(nextSort(salesSort, k))} />
                    <SortableTableHead label="Revenue" sortKey="totalRevenue" currentSort={salesSort} onSort={(k) => setSalesSort(nextSort(salesSort, k))} />
                    <SortableTableHead label="Commission" sortKey="totalCommission" currentSort={salesSort} onSort={(k) => setSalesSort(nextSort(salesSort, k))} />
                    <TableHead className="text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summaries.map((sp, i) => (
                    <TableRow key={sp.id}>
                      <TableCell className="text-muted-foreground font-mono text-xs">{i + 1}</TableCell>
                      <TableCell className="font-medium">{sp.name}</TableCell>
                      <TableCell>{sp.orderCount}</TableCell>
                      <TableCell>{sp.uniqueCustomers}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">{sp.activeSubscriptions}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">{fmt(sp.totalRevenue)}</TableCell>
                      <TableCell className="text-primary font-medium">{fmt(sp.totalCommission)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => setSelectedSp(sp)}>
                          <Eye size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Salesperson Detail Dialog */}
      <Dialog open={!!selectedSp} onOpenChange={(open) => !open && setSelectedSp(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {selectedSp?.name} — Order Details
            </DialogTitle>
          </DialogHeader>
          {selectedSp && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-secondary/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">Revenue</p>
                  <p className="font-display text-lg">{fmt(selectedSp.totalRevenue)}</p>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">Commission</p>
                  <p className="font-display text-lg text-primary">{fmt(selectedSp.totalCommission)}</p>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">Customers</p>
                  <p className="font-display text-lg">{selectedSp.uniqueCustomers}</p>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedSp.orders.map((o) => (
                    <TableRow key={o.id}>
                      <TableCell>
                        <p className="text-sm">{o.patient_name || "—"}</p>
                        <p className="text-xs text-muted-foreground">{o.patient_email || "—"}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs capitalize">{o.order_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={o.status === "active" ? "default" : o.status === "cancelled" || o.status === "refunded" ? "destructive" : "secondary"} className="text-xs capitalize">
                          {o.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{fmt(o.amount_cents)}</TableCell>
                      <TableCell className="text-xs">{o.commission_rate}%</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{format(new Date(o.created_at), "MMM d, yyyy")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesAttribution;
